import { useState } from 'react';

import { useConceptGroupExportInFlight } from './useConceptGroupExportInFlight';
import {
  MAX_EXPORT_CONCEPT_GROUPS,
  useConceptGroupExportSelection,
} from './useConceptGroupExportSelection';
import type { ProjectArtifactsGridSelection } from '../components/artifact-selection/selection';
import type {
  ConceptGroupExportPillProps,
  ConceptGroupsExportModalProps,
} from '../lib/types/conceptGroupExport';
import type { ProjectArtifact, ProjectArtifactFilterKind } from '../types';

export interface UseConceptGroupExportParams {
  projectId: string;
  artifacts: ProjectArtifact[];
  visibleArtifacts: ProjectArtifact[];
  activeFilter: ProjectArtifactFilterKind;
}

export interface UseConceptGroupExport {
  gridSelection: ProjectArtifactsGridSelection;
  /** Present on the concept-group tab; the pill draws itself once a row is picked. */
  pill: ConceptGroupExportPillProps | undefined;
  /** Present only while the export dialog is open. */
  exportModal: ConceptGroupsExportModalProps | undefined;
}

/**
 * The concept-group bulk export (OR-6580) as three render-ready values. Picking a
 * row is the only way to the pill.
 */
export const useConceptGroupExport = ({
  projectId,
  artifacts,
  visibleArtifacts,
  activeFilter,
}: UseConceptGroupExportParams): UseConceptGroupExport => {
  const isInFlight = useConceptGroupExportInFlight(projectId);
  const [isModalOpen, setModalOpen] = useState(false);

  const isAvailable = activeFilter === 'concept_group';
  const { selectedIds, selectedCount, selectableCount, gridSelection, clear } =
    useConceptGroupExportSelection({ artifacts, visibleArtifacts, enabled: isAvailable });

  // Plain objects, not memoized: neither consumer is memoized, so a stable identity
  // would buy nothing and cost a dependency list to keep honest.
  const pill: ConceptGroupExportPillProps | undefined = isAvailable
    ? {
        selectedCount,
        selectableCount,
        max: MAX_EXPORT_CONCEPT_GROUPS,
        isInFlight,
        onClear: clear,
        onExport: () => setModalOpen(true),
      }
    : undefined;

  const exportModal: ConceptGroupsExportModalProps | undefined = isModalOpen
    ? {
        projectId,
        conceptGroupIds: selectedIds,
        // The selection survives until close, so a retry inside the dialog still
        // has its ids.
        onClose: () => {
          setModalOpen(false);
          clear();
        },
      }
    : undefined;

  return { gridSelection, pill, exportModal };
};
